// RankedCard — renderer for viz: ranked. Two shapes:
//   Explorer (top_flow: rows_all + by_expiry) — a single sortable "Top
//     instruments" card. A Premium/Notional/Trades SORT toggle re-ranks rows_all
//     by the selected measure (Top-20 = top-N of the sorted set); a second row
//     picks the view (Top 20 / All / one button per live expiry). The active
//     sort column is bold. (Supersedes the separate most_active card — it's the
//     same table sorted by trades.)
//   Flat (unusual_activity: rows only) — no toggles; primary_metric drives the
//     bold column and, when it isn't one of the three measures (vol z-score),
//     gets its own leading emphasis column.

import { ReactNode, useContext, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import {
  fmtCount,
  fmtNotional,
  fmtUsd,
  futureExpiries,
  FlowMetric,
  maxAbs,
  METRIC_LABELS,
  METRIC_ORDER,
  rankedMetric,
  resolveNotionalUnit,
} from '../../../../providers/options-flow/options-flow.helpers';
import { OptionsFlowContext } from '../../../../providers/options-flow/options-flow-provider';
import { RankedReport, RankedRow } from '../../../../providers/options-flow/types';
import { FlowCard } from '../flow-card';
import { FlowEmptyState } from '../flow-empty-state';
import { DivergingBar } from '../diverging-bar';
import { ScrollWindowList } from '../scroll-window-list';
import { themedScrollbarSx } from '../table-chrome';

interface RankedCardProps {
  report: RankedReport;
}

// Exported so the windowing tests share the scroll-math row height (no drift).
export const ROW_H = 24;
const MONO = 'ui-monospace, Menlo, Consolas, monospace';
const TOP_N = 20;
// Fixed instrument-column width (px) — bounds the name (ellipsised) so the
// numeric columns pack right after it and the header stays aligned to the rows.
const INSTRUMENT_W = 168;

// The three sortable measure columns — derived from the canonical METRIC_ORDER
// (Notional · Premium · Trades) so the sort toggle AND the density columns track
// the shared triad. Each metric maps to its row field (`premium` -> premium_usd)
// and column width; the formatter is bound to the notional unit at render.
type SortKey = 'premium_usd' | 'notional' | 'trades';
const MEASURE_BY_METRIC: Record<FlowMetric, { key: SortKey; width: number }> = {
  notional: { key: 'notional', width: 70 },
  premium: { key: 'premium_usd', width: 62 },
  trades: { key: 'trades', width: 50 },
};
const MEASURE_META: { key: SortKey; label: string; width: number }[] = METRIC_ORDER.map((m) => ({
  key: MEASURE_BY_METRIC[m].key,
  label: METRIC_LABELS[m],
  width: MEASURE_BY_METRIC[m].width,
}));
const isSortKey = (v: unknown): v is SortKey => MEASURE_META.some((m) => m.key === v);

const thText = {
  fontSize: 9.5,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px',
  color: tokens.text.muted,
};

const cell = (value: number | null | undefined, fmt: (v: number) => string): string =>
  value == null || Number.isNaN(value) ? '-' : fmt(value);

const sortByMeasure = (rowsList: RankedRow[], key: SortKey): RankedRow[] =>
  [...rowsList].sort((a, b) => (b[key] ?? -Infinity) - (a[key] ?? -Infinity));

const ToggleButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <Box
    role="tab"
    aria-selected={active}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      flex: '0 0 auto',
      whiteSpace: 'nowrap',
      fontFamily: MONO,
      fontSize: 10,
      fontWeight: 700,
      px: '10px',
      py: '3px',
      borderRadius: '5px',
      color: active ? tokens.flow.accent : tokens.text.muted,
      backgroundColor: active ? tokens.bg.widget : 'transparent',
      border: `1px solid ${active ? tokens.flow.accent : tokens.border.default}`,
      '&:hover': { backgroundColor: tokens.bg.widget },
    }}
  >
    {children}
  </Box>
);

export const RankedCard = ({ report }: RankedCardProps) => {
  const metric = useMemo(() => rankedMetric(report.primary_metric), [report.primary_metric]);
  const rows = report.rows ?? [];
  const rowsAll = report.rows_all ?? [];
  const byExpiry = report.by_expiry ?? [];
  const hasExplorer = rowsAll.length > 0 && byExpiry.length > 0;

  const unit = resolveNotionalUnit(report.meta);
  // Density columns = MEASURE_META + a unit-bound formatter (single source).
  const fmtForKey: Record<SortKey, (v: number) => string> = {
    premium_usd: fmtUsd,
    notional: (v) => fmtNotional(v, unit),
    trades: fmtCount,
  };
  const measures = MEASURE_META.map((m) => ({ ...m, fmt: fmtForKey[m.key] }));

  // Sort measure (explorer only). Default to the report's primary_metric when it
  // is one of the three, else premium. Typed SortKey throughout, so the state is
  // always valid — no runtime guard needed.
  const defaultSort: SortKey = isSortKey(report.primary_metric)
    ? report.primary_metric
    : 'premium_usd';
  const [sort, setSort] = useState<SortKey>(defaultSort);

  // Live expiries only, chronological (settlement-aware; active venue's hour).
  // One button each.
  const { activeVenue } = useContext(OptionsFlowContext);
  const settlementHour = activeVenue?.settlement_hour_utc;
  const expiryButtons = useMemo(
    () => futureExpiries(byExpiry, undefined, settlementHour),
    [byExpiry, settlementHour],
  );

  // Active view: 'top' | 'all' | an expiry label.
  const [view, setView] = useState<string>('top');

  // The bold column: the active sort for the explorer, else primary_metric.
  const boldKey: string = hasExplorer ? sort : metric.key;
  // Non-explorer only: an extra emphasis column when primary_metric isn't a measure.
  const extraMetric = hasExplorer || measures.some((m) => m.key === metric.key) ? null : metric;

  const sortedAll = useMemo(
    () => (hasExplorer ? sortByMeasure(rowsAll, sort) : rows),
    [hasExplorer, rowsAll, rows, sort],
  );
  const displayRows = !hasExplorer
    ? rows
    : view === 'top'
      ? sortedAll.slice(0, TOP_N)
      : view === 'all'
        ? sortedAll
        : sortedAll.filter((r) => r.expiry === view);
  const windowed = hasExplorer && view === 'all';

  const numHeadSx = (bold: boolean) => ({
    ...thText,
    textAlign: 'right' as const,
    color: bold ? tokens.text.secondary : tokens.text.muted,
  });
  const numCellSx = (bold: boolean) => ({
    textAlign: 'right' as const,
    fontFamily: MONO,
    fontSize: 11.5,
    fontWeight: bold ? 700 : 500,
    color: bold ? tokens.text.primary : tokens.text.secondary,
  });

  // One shared grid for the header + every data row so columns line up exactly.
  // Fixed left cluster (# · instrument · the numeric measures) packs tight; BIAS
  // takes the flexible remaining width (1fr) so its net-taker-bias bar is long
  // and readable rather than leaving a middle void. All widths are fixed (not
  // content-sized) so independent row grids stay aligned with the header.
  const gridTemplateColumns = [
    '22px', // #
    `${INSTRUMENT_W}px`, // instrument (ellipsised)
    ...(extraMetric ? ['58px'] : []),
    ...measures.map((m) => `${m.width}px`),
    'minmax(110px, 1fr)', // bias — fills the rest
  ].join(' ');

  const rowGridSx = {
    display: 'grid',
    gridTemplateColumns,
    alignItems: 'center',
    columnGap: '8px',
    px: '6px',
  } as const;

  const header = (
    <Box sx={{ ...rowGridSx, py: '3px', borderBottom: `1px solid ${tokens.border.subtle}` }}>
      <Box sx={thText}>#</Box>
      <Box sx={{ minWidth: 0, ...thText }}>Instrument</Box>
      {extraMetric && <Box sx={numHeadSx(true)}>{extraMetric.label}</Box>}
      {measures.map((m) => (
        <Box key={m.key} sx={numHeadSx(m.key === boldKey)}>
          {m.label}
        </Box>
      ))}
      <Box sx={thText}>Bias</Box>
    </Box>
  );

  const renderRow = (r: RankedRow, i: number, scale: number) => (
    <Box sx={{ ...rowGridSx, height: ROW_H }}>
      <Box sx={{ color: tokens.text.muted, fontFamily: MONO, fontSize: 10.5 }}>
        {String(i + 1).padStart(2, '0')}
      </Box>
      <Box
        sx={{
          minWidth: 0,
          fontFamily: MONO,
          fontSize: 10.5,
          color: tokens.text.secondary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {r.instrument}
      </Box>
      {extraMetric && (
        <Box sx={numCellSx(true)}>
          {extraMetric.format(r[extraMetric.key as keyof RankedRow] as number | undefined)}
        </Box>
      )}
      {measures.map((m) => (
        <Box key={m.key} sx={numCellSx(m.key === boldKey)}>
          {cell(r[m.key], m.fmt)}
        </Box>
      ))}
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
        <DivergingBar value={r.bias} max={scale} height={12} fill />
      </Box>
    </Box>
  );

  const listView = (data: RankedRow[], isWindowed: boolean) => {
    if (data.length === 0) return <FlowEmptyState />;
    const scale = maxAbs(data.map((r) => r.bias));
    return (
      <Box>
        {header}
        {isWindowed ? (
          <ScrollWindowList
            items={data}
            rowHeight={ROW_H}
            height={360}
            keyFor={(r) => r.instrument}
            renderRow={(r, i) => renderRow(r, i, scale)}
            testId="ranked-all-list"
          />
        ) : (
          <Box sx={{ maxHeight: 360, overflowY: 'auto', ...themedScrollbarSx }}>
            {data.map((r, i) => (
              <Box key={r.instrument}>{renderRow(r, i, scale)}</Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const sortLabel = measures.find((m) => m.key === sort)?.label.toLowerCase() ?? 'premium';

  return (
    <FlowCard
      title={report.title}
      caption={
        hasExplorer
          ? `Sorted by ${sortLabel} · bar = net taker bias (buy ▸ / ◂ sell)`
          : `Ranked by ${metric.label.toLowerCase()} · bar = net taker bias (buy ▸ / ◂ sell)`
      }
      // Explorer (top_flow) follows the global window; a flat ranked card
      // (unusual_activity) is window-independent -> "live".
      windowOverride={hasExplorer ? undefined : 'live'}
    >
      {hasExplorer && (
        <>
          {/* Sort toggle — re-ranks by the selected measure. */}
          <Stack direction="row" spacing={0.75} role="tablist" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
            {measures.map((m) => (
              <ToggleButton key={m.key} active={sort === m.key} onClick={() => setSort(m.key)}>
                {m.label}
              </ToggleButton>
            ))}
          </Stack>
          {/* View toggle — Top-N / All / per live expiry. */}
          <Stack
            direction="row"
            spacing={0.75}
            role="tablist"
            sx={{ mb: 1, overflowX: 'auto', pb: '4px', ...themedScrollbarSx }}
          >
            <ToggleButton active={view === 'top'} onClick={() => setView('top')}>
              Top {TOP_N}
            </ToggleButton>
            <ToggleButton active={view === 'all'} onClick={() => setView('all')}>
              All{report.n_instruments ? ` (${report.n_instruments})` : ''}
            </ToggleButton>
            {expiryButtons.map((e) => (
              <ToggleButton
                key={e.expiry_date + e.expiry}
                active={view === e.expiry}
                onClick={() => setView(e.expiry)}
              >
                {e.expiry}
              </ToggleButton>
            ))}
          </Stack>
        </>
      )}

      {listView(displayRows, windowed)}
    </FlowCard>
  );
};
